import { ModalParams, ModalType } from 'constants/modal';

type Listeners = {
  [key in ModalType]?: EventListener<key> | null;
};

type EventListener<T extends ModalType> = (isShow: boolean, p?: ModalParams[T]) => void;

type QueueItem<T extends ModalType> = {
  name: T;
  params?: ModalParams[T];
  type: 'show' | 'hide';
};

export class ModalHandler {
  private static listeners: Listeners = {};

  private static queue: QueueItem<ModalType>[] = [];

  private static queueInterval: ReturnType<typeof setInterval> | null = null;

  static show<T extends ModalType>(name: T, params?: ModalParams[T]) {
    const listener = this.listeners[name] as EventListener<T>;
    if (listener) {
      listener(true, params);
    } else {
      // 리스너가 없으면 큐에 저장
      this.addToQueue({ name, params, type: 'show' });
    }
  }

  static hide<T extends ModalType>(name: T, params?: ModalParams[T]) {
    const listener = this.listeners[name] as EventListener<T>;
    listener?.(false, params);
  }

  static addEventListener<T extends ModalType>(eventName: T, listener: Listeners[T]) {
    this.listeners[eventName] = listener;
    this.processQueue();
  }

  static removeEventListener(eventName: ModalType) {
    this.listeners[eventName] = null;
  }

  private static addToQueue<T extends ModalType>(item: QueueItem<T>) {
    this.queue.push(item);

    if (!this.queueInterval) {
      this.queueInterval = setInterval(() => {
        this.processQueue();
      }, 500);
    }
  }

  private static processQueue() {
    if (this.queue.length === 0) {
      if (this.queueInterval) {
        clearInterval(this.queueInterval);
        this.queueInterval = null;
      }
      return;
    }

    this.queue = this.queue.filter(item => {
      const listener = this.listeners[item.name] as EventListener<typeof item.name>;
      if (listener) {
        listener(item.type === 'show', item.params);
        return false;
      }
      return true;
    });
  }
}
